import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, HelpCircle, Mail, MessageCircle, Camera, Music, CreditCard,
  Trash2, UserCheck, AlertTriangle,
} from "lucide-react";
import { SUPPORT_EMAIL } from "../lib/constants";

/**
 * Public support page rendered at `/support`. This is the URL we submit
 * to Apple's App Store Connect "Support URL" field, so it must:
 *   - Be reachable without authentication
 *   - Provide a clear way to contact us (mailto:)
 *   - Cover the most common user pain points
 *
 * Kept intentionally lean — every FAQ has a 1-2 sentence answer linking
 * to the relevant in-app screen or to a deeper doc when needed.
 */
const Faq = ({ icon: Icon, q, children, testid }) => (
  <details
    className="group border border-white/10 hover:border-white/20 transition-colors mb-3"
    data-testid={testid}
  >
    <summary className="cursor-pointer list-none p-4 flex items-center gap-3">
      <Icon className="w-4 h-4 text-[#6B9BFF] shrink-0" />
      <span className="flex-1 font-bold text-white text-sm">{q}</span>
      <span className="text-white/40 text-xs uppercase tracking-widest group-open:hidden">Open</span>
      <span className="text-white/40 text-xs uppercase tracking-widest hidden group-open:inline">Close</span>
    </summary>
    <div className="px-4 pb-4 -mt-1 text-white/70 text-sm leading-relaxed space-y-2 border-t border-white/5 pt-3">
      {children}
    </div>
  </details>
);

export default function SupportPage() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="support-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-white/50 hover:text-white mb-8"
          data-testid="support-back-home"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to home
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <HelpCircle className="w-5 h-5 text-[#6B9BFF]" />
          <div className="label-eyebrow text-[#6B9BFF]">Support</div>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-none mb-4">
          We've got you.
        </h1>
        <p className="text-white/60 text-sm sm:text-base mb-12 max-w-2xl">
          Most questions are answered below. If you can't find what you're looking for,
          drop us an email and a real human will get back to you within 1 business day.
        </p>

        <a
          href={`mailto:${SUPPORT_EMAIL}?subject=DAIONE%20Support`}
          className="inline-flex items-center gap-3 bg-[#6B9BFF] text-black font-bold uppercase tracking-widest text-xs px-6 py-4 hover:bg-white transition-colors mb-12"
          data-testid="support-email-cta"
        >
          <Mail className="w-4 h-4" /> Email {SUPPORT_EMAIL}
        </a>

        <h2 className="font-display text-2xl sm:text-3xl text-white mb-4">Top questions</h2>

        <Faq icon={UserCheck} q="How do I reset my password?" testid="faq-password">
          <p>
            From the sign-in screen, tap <strong className="text-white">Forgot password</strong> and enter
            the email you registered with. You'll receive a reset link within a minute or two
            (check your spam folder if it doesn't arrive).
          </p>
        </Faq>

        <Faq icon={MessageCircle} q="The AI workout isn't matching my equipment / level" testid="faq-ai-workout">
          <p>
            DAIONE uses your <strong className="text-white">Profile</strong> + your selected
            equipment + favourite exercises to generate a plan. Make sure those are up to date
            on the Profile page, then tap <strong className="text-white">Regenerate</strong> on the
            Workout tab. Each regeneration uses a Pro credit during your trial.
          </p>
        </Faq>

        <Faq icon={Camera} q="The camera / photo permission isn't working" testid="faq-camera">
          <p>
            We only use the camera to log meals or record private progress photos. If you've
            previously declined the permission, you'll need to re-enable it from your iOS
            <strong className="text-white"> Settings → DAIONE → Camera / Photos</strong>. The app
            still works fully without these permissions — just use the text-based food log
            and skip progress photos.
          </p>
        </Faq>

        <Faq icon={Music} q="Spotify won't connect or won't play music" testid="faq-spotify">
          <p>
            The in-app player streams via the official Spotify Web Playback SDK. Two
            requirements:
          </p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>You need a Spotify <strong className="text-white">Premium</strong> account
            (Spotify's restriction, not ours).</li>
            <li>Your Spotify account must be linked from
            <strong className="text-white"> Profile → Spotify</strong>.</li>
          </ol>
          <p>
            Free Spotify accounts can still browse playlists and search in DAIONE —
            playback is the only blocked feature.
          </p>
        </Faq>

        <Faq icon={CreditCard} q="How do I manage or cancel my subscription?" testid="faq-billing">
          <p>
            Open <strong className="text-white">Billing</strong> from the side menu. You'll see
            your trial / subscription status and a <strong className="text-white">Manage</strong>
            {" "}button that takes you to the Stripe customer portal where you can update card
            details or cancel. Cancelling stops the next renewal — you keep Pro until the
            current period ends.
          </p>
          <p className="text-white/50 text-xs">
            Note: iOS subscribers will be moved to Apple's in-app purchases in a near-term
            update. Existing Stripe customers will be honoured for the lifetime of their plan.
          </p>
        </Faq>

        <Faq icon={Trash2} q="How do I delete my account and all my data?" testid="faq-delete-account">
          <p>
            Profile → scroll to <strong className="text-white">Delete account</strong>. This
            permanently wipes your workouts, food logs, weight history, progress photos, and
            badges. You can also request deletion by email — we'll process within 7 days.
          </p>
        </Faq>

        <Faq icon={AlertTriangle} q="I think DAIONE has a bug. How do I report it?" testid="faq-bug">
          <p>
            Send an email to <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#6B9BFF] underline">{SUPPORT_EMAIL}</a> with
            (a) a screenshot or screen recording, (b) your iOS version, (c) what you were
            trying to do. We triage every report and ship critical fixes within days.
          </p>
        </Faq>

        <h2 className="font-display text-2xl sm:text-3xl text-white mb-4 mt-12">Still stuck?</h2>
        <p className="text-white/70 text-sm sm:text-base mb-6">
          Email{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#6B9BFF] underline">{SUPPORT_EMAIL}</a>{" "}
          and we'll be in touch.
        </p>

        <div className="text-white/40 text-xs uppercase tracking-widest pt-8 border-t border-white/5">
          <Link to="/privacy" className="hover:text-white mr-6" data-testid="support-link-privacy">Privacy</Link>
          <Link to="/terms" className="hover:text-white" data-testid="support-link-terms">Terms</Link>
        </div>
      </div>
    </div>
  );
}
